import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Accordion,
  AccordionSet,
  AccordionStatus,
  Button,
  Col,
  ExpandAllButton,
  Icon,
  IconButton,
  KeyValue,
  List,
  Loading,
  Pane,
  Row,
} from '@folio/stripes-components';
import { CalloutContext, Pluggable, useOkapiKy } from '@folio/stripes/core';
import { getLocaleLabel, ImportLocalesIcones, getFullName } from '../../utils/utils';

function LanguagesTranslatorsDetail({
  record,
  usersMap,
  onSave,
  onUpdateUsersMap,
  onClose,
}) {
  const intl = useIntl();
  const flags = ImportLocalesIcones();
  const okapiKy = useOkapiKy();
  const callout = useContext(CalloutContext);

  const [translators, setTranslators] = useState(record?.translators || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTranslators(record?.translators || []);
  }, [record?.id]);

  const save = async (newTranslators) => {
    setSaving(true);
    try {
      const payload = {
        id: record.id,
        localeValue: record.localeValue,
        translators: newTranslators,
      };
      await okapiKy.put(`languageTranslators/${record.id}`, { json: payload });
      await onSave();
      callout?.sendCallout({
        type: 'success',
        message: (
          <FormattedMessage
            id="ui-translations.settings.callout.updatedLanguages.success"
          />
        ),
      });
    } catch (e) {
      callout?.sendCallout({
        type: 'error',
        message: e.message || 'Error saving translators',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSelectUser = (user) => {
    if (translators.includes(user.id)) return;
    const newTranslators = [...translators, user.id];
    setTranslators(newTranslators);
    // Add to Manager's usersMap immediately - no flash
    onUpdateUsersMap(user);
    save(newTranslators);
  };

  const handleRemove = (userId) => {
    const newTranslators = translators.filter((id) => id !== userId);
    setTranslators(newTranslators);
    save(newTranslators);
  };

  const paneTitle = (
    <Icon icon={flags[record.localeValue] || 'flag'}>
      {getLocaleLabel(record.localeValue, intl)}
    </Icon>
  );

  return (
    <Pane
      defaultWidth="fill"
      paneTitle={paneTitle}
      dismissible
      onClose={onClose}
    >
      {saving && <Loading />}
      <AccordionStatus>
        <Row end="xs">
          <Col xs>
            <ExpandAllButton />
          </Col>
        </Row>
        <AccordionSet>
          <Accordion
            id="lang-trans-general"
            label={
              <FormattedMessage
                id="ui-translations.settings.languagesTranslators.accordion.general"
                defaultMessage="General Information"
              />
            }
          >
            <Row>
              <Col xs={12}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-translations.settings.languages.columns.name"
                    />
                  }
                  value={paneTitle}
                />
              </Col>
            </Row>
          </Accordion>

          <Accordion
            id="lang-trans-translators"
            label={
              <FormattedMessage
                id="ui-translations.settings.languagesTranslators.columns.translators"
                defaultMessage="Translators"
              />
            }
          >
            <Row>
              <Col xs={12}>
                <Pluggable
                  aria-haspopup="true"
                  type="find-user"
                  id={`clickable-find-translator-${record.localeValue}`}
                  dataKey={`find-translator-${record.localeValue}`}
                  searchButtonStyle="default"
                  searchLabel={
                    <>
                      <Icon icon="plus-sign" />
                      {' '}
                      <FormattedMessage
                        id="ui-translations.settings.languagesTranslators.addTranslator"
                        defaultMessage="Add Translator"
                      />
                    </>
                  }
                  selectUser={handleSelectUser}
                  visibleColumns={['status', 'name', 'patronGroup', 'username', 'barcode']}
                  disableRecordCreation
                >
                  <Button buttonStyle="default" disabled={saving}>
                    <Icon icon="plus-sign" />
                    {' '}
                    <FormattedMessage
                      id="ui-translations.settings.languagesTranslators.addTranslator"
                      defaultMessage="Add Translator"
                    />
                  </Button>
                </Pluggable>
              </Col>
            </Row>

            <Row style={{ marginTop: '1rem' }}>
              <Col xs={12}>
                {translators.length > 0 ? (
                  <List
                    listStyle="bullets"
                    items={translators}
                    itemFormatter={(userId) => (
                      <li key={userId}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Icon icon="profile">
                            {usersMap[userId]
                              ? getFullName(usersMap[userId])
                              : userId}
                          </Icon>
                          <IconButton
                            icon="trash"
                            size="small"
                            disabled={saving}
                            onClick={() => handleRemove(userId)}
                          />
                        </div>
                      </li>
                    )}
                  />
                ) : (
                  <div style={{ padding: '8px 0', color: '#666' }}>
                    <FormattedMessage
                      id="ui-translations.settings.languagesTranslators.noTranslators"
                      defaultMessage="No translators assigned"
                    />
                  </div>
                )}
              </Col>
            </Row>
          </Accordion>
        </AccordionSet>
      </AccordionStatus>
    </Pane>
  );
}

LanguagesTranslatorsDetail.propTypes = {
  record: PropTypes.object.isRequired,
  usersMap: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onUpdateUsersMap: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default LanguagesTranslatorsDetail;
